import { useEffect, useRef } from "react";

/**
 * Invokes `callback` whenever the tab returns to the foreground — on
 * `visibilitychange` to visible and on window `focus`.
 *
 * The SignalR alert/telemetry stream is at-most-once: messages produced while
 * the tab is backgrounded (or during transient gaps) can be dropped, so the
 * store slowly drifts from the backend. Reconciling on return-to-foreground
 * restores consistency right when the user looks, without a manual refresh and
 * without constant background polling.
 *
 * Calls are throttled (default 30s) so rapid tab switching does not trigger a
 * burst of refetches. The latest `callback` is always used (ref), so it sees
 * current props/state without re-subscribing.
 *
 * `callback` is invoked with the number of milliseconds the tab was hidden for
 * (0 if it wasn't hidden, e.g. a plain window focus) — callers that also need
 * to detect a long background suspension (mobile browsers freeze JS timers
 * while backgrounded) can use this instead of re-tracking visibility state
 * themselves.
 *
 * @param {function} callback - invoked with (hiddenMs) when the tab becomes visible/focused
 * @param {{ throttleMs?: number }} [options]
 */
export const useReconcileOnVisible = (callback, { throttleMs = 30000 } = {}) => {
  const callbackRef = useRef(callback);
  const lastRunRef = useRef(0);
  const hiddenAtRef = useRef(null);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    // Throttle gate only — hiddenMs is always resolved by the caller at the
    // actual state transition (see onVisibility/onFocus below). Resolving it
    // in here instead would leave hiddenAtRef unset whenever the throttle
    // short-circuits, so a later unrelated call could pick up a stale
    // timestamp and report an inflated duration.
    const run = (hiddenMs = 0) => {
      const now = Date.now();
      if (now - lastRunRef.current < throttleMs) return;
      lastRunRef.current = now;
      callbackRef.current(hiddenMs);
    };

    const onFocus = () => {
      if (
        typeof document !== "undefined" &&
        document.visibilityState === "hidden"
      ) {
        return;
      }
      run();
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        const hiddenMs = hiddenAtRef.current
          ? Date.now() - hiddenAtRef.current
          : 0;
        hiddenAtRef.current = null;
        run(hiddenMs);
      } else if (document.visibilityState === "hidden") {
        hiddenAtRef.current = Date.now();
      }
    };

    globalThis.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      globalThis.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [throttleMs]);
};
